import { CommonModule } from "@angular/common";
import { Component, inject, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule } from "@ngx-translate/core";
import { StoreService } from "src/app/services/apps/store/store.service";
import { ContactUsService } from "./contact-us.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { LoaderComponent } from "src/app/shared/components/loader/loader.component";
import { ContactUsDialogData, IssueTypeOption } from "./model";
import { StoreGetDTO } from "src/app/pages/store-management/stores/store";
import { AppConfigService } from "src/app/core/appConfigs/app-config.service";
import { from, throwError } from "rxjs";
import {
  catchError,
  concatMap,
  finalize,
  switchMap,
  tap,
  toArray,
} from "rxjs/operators";
import { TablerIconsModule } from "angular-tabler-icons";
import { ViewContextHistoryStateService } from "../../services/view-context-history-state.service";

@Component({
  selector: "app-contact-us",
  templateUrl: "./contact-us.component.html",
  styleUrls: ["./contact-us.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,
    LoaderComponent,
    TablerIconsModule,
  ],
})
export class ContactUsComponent implements OnInit {
  selectedFiles: Array<{
    file: File;
    fileId?: string;
    fileName: string;
    extension: string;
    isImage: boolean;
    previewUrl?: string;
  }> = [];

  supportingFileIds: string[] = [];
  uploadError?: string;

  private readonly appConfigService = inject(AppConfigService);

  get maxSizeFileUpload(): number {
    return this.appConfigService.appConfigs()?.contactSupportConfigs
      .maxSizeFileUploadBytes!;
  }

  get allowedFilesAmountPerTicket(): number {
    return this.appConfigService.appConfigs()?.contactSupportConfigs
      .allowedFilesAmountPerTicket!;
  }

  issueTypeOptions: IssueTypeOption[] = [
    {
      value: "bug_report",
      translationKey: "contact_us_support.issue_types.bug_report",
    },
    {
      value: "feature_request",
      translationKey: "contact_us_support.issue_types.feature_request",
    },
    {
      value: "billing_issue",
      translationKey: "contact_us_support.issue_types.billing_issue",
    },
    {
      value: "account_access",
      translationKey: "contact_us_support.issue_types.account_access",
    },
    {
      value: "data_inquiry",
      translationKey: "contact_us_support.issue_types.data_inquiry",
    },
    {
      value: "integration_help",
      translationKey: "contact_us_support.issue_types.integration_help",
    },
    {
      value: "general_inquiry",
      translationKey: "contact_us_support.issue_types.general_inquiry",
    },
  ];
  stores: StoreGetDTO[] = [];
  contactForm: FormGroup;
  isUploadingFiles = false;
  viewContextHistoryStateSvc = inject(ViewContextHistoryStateService);
  constructor(
    private fb: FormBuilder,
    private storeService: StoreService,
    private contactUsService: ContactUsService,
    private loaderService: LoaderService,
    private dialogRef: MatDialogRef<ContactUsComponent>,
    @Inject(MAT_DIALOG_DATA) private data: ContactUsDialogData,
  ) {
    this.contactForm = this.buildForm();
  }

  ngOnInit(): void {
    if (this.data?.stores?.length) {
      this.stores = this.data.stores;
      this.applyFormDefaults();
    } else {
      this.storeService.get().subscribe((stores: any) => {
        this.stores = stores;
        this.applyFormDefaults();
      });
    }
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      storeId: [""],
      category: ["general_inquiry", Validators.required],
      description: ["", Validators.required],
      supporting_files: this.fb.array([]),
    });
  }

  private applyFormDefaults(): void {
    if (!this.stores.length) {
      return;
    }

    const storeId =
      this.data?.storeId &&
      this.stores.some((store) => store.storeId === this.data.storeId)
        ? this.data.storeId
        : this.stores[0].storeId;

    this.contactForm.patchValue({
      storeId,
      ...(this.data?.category ? { category: this.data.category } : {}),
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];

    if (!files.length) {
      return;
    }

    const availableSlots =
      this.allowedFilesAmountPerTicket - this.selectedFiles.length;

    if (availableSlots <= 0) {
      this.uploadError = "contact_us_support.errors.max_files_reached";
      return;
    }

    if (files.length > availableSlots) {
      this.uploadError = "contact_us_support.errors.files_limit_exceeded";
      return;
    }

    this.uploadError = undefined;

    for (const file of files) {
      if (file.size > this.maxSizeFileUpload) {
        this.uploadError = "contact_us_support.errors.file_too_large";
        continue;
      }

      const alreadySelected = this.selectedFiles.some(
        (selected) =>
          selected.file.name === file.name && selected.file.size === file.size,
      );

      if (alreadySelected) {
        this.uploadError = "contact_us_support.errors.duplicate_file";
        setTimeout(() => {
          this.uploadError = undefined;
        }, 2000);
        continue;
      }

      const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
      const isImage =
        file.type.startsWith("image/") ||
        ["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(extension);

      this.selectedFiles.push({
        file,
        fileName: file.name,
        extension: extension.toUpperCase(),
        isImage,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      });
    }

    input.value = "";
  }

  removeSupportingFile(index: number): void {
    const file = this.selectedFiles[index];

    if (file?.fileId) {
      const fileIdIndex = this.supportingFileIds.indexOf(file.fileId);
      if (fileIdIndex !== -1) {
        this.supportingFileIds.splice(fileIdIndex, 1);
      }
    }

    if (index >= 0 && index < this.selectedFiles.length) {
      this.selectedFiles.splice(index, 1);
    }
  }

  submit(): void {
    if (this.contactForm.invalid || this.isUploadingFiles) {
      return;
    }
    this.isUploadingFiles = true;
    this.loaderService.show();
    this.supportingFileIds = [];

    const storeId = this.contactForm.get("storeId")?.value;
    const filesToUpload = this.selectedFiles;

    const uploadAndSubmit$ = from(filesToUpload).pipe(
      concatMap((fileEntry) =>
        this.contactUsService.uploadFile(storeId, fileEntry.file).pipe(
          tap((fileId) => {
            fileEntry.fileId = fileId;
            this.supportingFileIds.push(fileId);
          }),
          catchError((err) => {
            this.uploadError = "contact_us_support.errors.upload_failed";
            return throwError(err);
          }),
        ),
      ),
      toArray(),
      switchMap(() =>
        this.contactUsService.sendTicket({
          storeId,
          category: this.contactForm.get("category")?.value,
          description: this.contactForm.get("description")?.value,
          supporting_files: this.supportingFileIds,
          metadata: JSON.stringify({
            // url:
            //   environment.baseAppUrl +
            //   this.viewContextHistoryStateSvc.getCurrentPath(),
            viewHistoryState:
              this.viewContextHistoryStateSvc.getContextHistory(),
          }).replace(/"/g, "'"),
        }),
      ),
      finalize(() => {
        this.isUploadingFiles = false;
        this.loaderService.hide();
      }),
    );

    uploadAndSubmit$.subscribe({
      next: () => {
        this.dialogRef.close({ success: true });
      },
      error: () => {
        this.uploadError =
          this.uploadError || "contact_us_support.errors.submit_failed";
      },
    });
  }
}
